/* © CNES */
import { QueryFunctionContext, useQueries } from '@tanstack/react-query';
import { Paginator } from 'primereact/paginator';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Toast } from 'primereact/toast';
import { Tree, TreeExpandedKeysType } from 'primereact/tree';
import { TreeNode } from 'primereact/treenode';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  deleteSavedProduct,
  getSavedProducts,
  restoreArchivedProduct,
  saveSelectedProducts,
} from '../../../../api/cart/Cart';
import { useGetItemsAvailabilities } from '../../../../api/items/useGetItemAvailability';
import { useGetItems } from '../../../../api/items/UseItemsQuery';
import { ConfigContext } from '../../../../context/ConfigContext';
import { FilterDataContext } from '../../../../context/FilterDataContext';
import {
  useDataToTree,
  useFormattedCollections,
  useSortedSatelliteCollections,
} from '../../../../hooks/CustomHooks';
import { CollectionDetails } from '../../../../models/CollectionDetails';
import { Feature } from '../../../../models/Feature';
import { ProductsAdded } from '../../../../models/ProductsAdded';
import { SatelliteDataItem } from '../../../../models/SatelliteDataItem';
import { DATASET, END_POINT_URL } from '../../../shared/Constants';
import useUrlUpdater from '../../../shared/hooks/useUrlUpdater';
import { getProductsAddedObject } from '../../../shared/Utils';
import { NodeTemplate, TogglerTemplate } from './NodeTemplate';
import { PaginatorTemplate } from './PaginatorTemplate';
import './Results.scss';
import {
  addAllProductsFromProductType,
  buildResultsSets,
  getProductHREF,
  removeAllProductsFromNode,
} from './utils';

/**
 * Results Props
 */
interface ResultsProps {
  setFiltersVisibility: Function;
  setIsDatalakeAvailable: Function;
  isDatalakeAvailable: string;
  setMaxProductsWarningVisible: Function;
}

/**
 * Results displays items from regards as a tree with nodes for missions and product types.
 * @param setFiltersVisibility set boolean values when switch to result view.
 * @param setIsDatalakeAvailable function setIsDatalakeAvailable.
 * @param isDatalakeAvailable datalake availibility status.
 * @param setMaxProductsWarningVisible function to setMaxProductsWarningVisible.
 * @returns Results Element.
 */
export const Results = ({
  setFiltersVisibility,
  setIsDatalakeAvailable,
  isDatalakeAvailable,
  setMaxProductsWarningVisible,
}: ResultsProps) => {
  const navigate = useNavigate();
  const context = useContext(ConfigContext);
  const { filterData } = useContext(FilterDataContext);
  const [page, setPage] = useState(context.queryPage);
  const [nextToken, setNextToken] = useState(context.nextToken);
  const [rows, setRows] = useState(20);
  const [first, setFirst] = useState((context.queryPage - 1) * rows);
  const [selectedProducts, setSelectedProducts] = useState<{
    productsName: Array<string>;
    operationType: string;
  }>({
    productsName: [],
    operationType: '',
  });
  const productRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Updates the URL parameters based on changes in the map extent, drawn shapes, or filter data
  useUrlUpdater();

  const { isFetching, data } = useGetItems(
    {
      params: filterData,
      token: nextToken,
      bbox:
        context.drawnShape ??
        (context.mapExtent.currentView ? context.mapExtent.extent : undefined),
      intersects: context.searchZone.value,
    },
    navigate,
    context.setError400
  ); 
  const {
    data: itemsAvailabilities,
    isSuccess: isAvailabilitiesSuccess,
    isLoading: isAvailabilitiesLoading,
  } = useGetItemsAvailabilities({availability: data?.features});

  const toast = useRef<Toast>(null);

  /**
   * messageDisplay
   * @param numberOfProduct number of products affected by action ADDED or REMOVED.
   * @param message action ADDED or REMOVED.
   */
  const messageDisplay = (numberOfProduct: number, message: string) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Success',
      detail: `${numberOfProduct} ${message}`,
      life: 2000,
    });
  };

  const isAuthDisabled = process.env.NODE_ENV !== 'production';

  const isUserNotLogged: boolean = useMemo(() => {
    return !isAuthDisabled && !context.userLoggedIn.email;
  }, [context.userLoggedIn.email, isAuthDisabled]);

  const satelliteCollections: SatelliteDataItem[] =
    useSortedSatelliteCollections(data?.features);

  const formattedCollectionsList: CollectionDetails[] = useFormattedCollections(
    data?.features,
    itemsAvailabilities
  );

  const {
    treeNodes,
    keysToExpand,
  }: { treeNodes: TreeNode[]; keysToExpand: TreeExpandedKeysType } =
    useDataToTree(satelliteCollections);

  useEffect(() => {
    if (isAvailabilitiesLoading) {
      setIsDatalakeAvailable('loading');
    } else if (isAvailabilitiesSuccess && itemsAvailabilities?.status !== 500) {
      context.setItemsAvailabilities(itemsAvailabilities);
      setIsDatalakeAvailable('available');
    } else {
      setIsDatalakeAvailable('unavailable');
    }
    // eslint-disable-next-line
  }, [
    isAvailabilitiesLoading,
    isAvailabilitiesSuccess,
    itemsAvailabilities?.status,
  ]);

  /**
   * clean Hierarchical Tree By selected Products.
   * @param collectionProp 'satellite' | 'level'
   */
  const cleanHierarchicalTreeByProducts = (
    collectionProp: 'satellite' | 'level'
  ) => {
    const hierarchicalSet = new Set(
      satelliteCollections.map((collection) => collection[collectionProp])
    );

    hierarchicalSet.forEach((hierarchicalName) => {
      const allCollectionsForSetName = satelliteCollections.filter(
        (collection) => {
          return collection[collectionProp] === hierarchicalName;
        }
      );

      const isHierarchicalCollectionsExhaustive =
        allCollectionsForSetName.reduce(
          (isCollectionInProducts, collection) => {
            if (!context.productsAdded.products.includes(collection.title)) {
              isCollectionInProducts = false;
            }
            return isCollectionInProducts;
          },
          true
        );

      if (
        !isHierarchicalCollectionsExhaustive &&
        context.productsAdded.results.includes(hierarchicalName)
      ) {
        const hierarchicalNameIndex =
          context.productsAdded.results.indexOf(hierarchicalName);
        context.productsAdded.results.splice(hierarchicalNameIndex, 1);
      } else if (
        isHierarchicalCollectionsExhaustive &&
        !context.productsAdded.results.includes(hierarchicalName)
      ) {
        context.productsAdded.results.push(hierarchicalName);
      }
    });
  };

  /**
   *  Scroll to the selected product in Results list when user click
   * on map to select a polygon.
   */
  useEffect(() => {
    calculateProductPosition(context.selectedProduct.productName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.selectedProduct]);

  /**
   * set Filters Visibility to false when Results reviewed.
   */
  useEffect(() => {
    setFiltersVisibility(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * clean Tree nodes selection on page change or items list change retrieved from regards.
   */
  useEffect(() => {
    context.setQueryPage(page);
    //context.setNextToken(nexttoken);
    //context.setPreviousToken(previoustoken);

    // clean satellite and level from context results if context products list is not exhaustive
    if (satelliteCollections?.length) {
      cleanHierarchicalTreeByProducts('satellite');
      cleanHierarchicalTreeByProducts('level');

      // clean products on page change
      const productsCleaned = [...context.productsAdded.products].filter(
        (product) => {
          return (
            !context.productsAdded.levelSet.has(product) ||
            !context.productsAdded.satelliteSet.has(product)
          );
        }
      );

      context.setProductsAdded({
        ...context.productsAdded,
        products: productsCleaned,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, satelliteCollections]);

  /**
   * Show Toast When Products are Added Or Removed.
   * @param products string[] products added during user selection of items.
   * @param operationType string show toast depending on case ex: ADDED or REMOVED
   */
  const showToastWhenProductsAddedOrRemoved = (
    products: string[],
    operationType: string
  ) => {
    messageDisplay(products.length, `Data ${operationType}`);
  };

  /**
   * save Products Added on click.
   * @param results all results from items search.
   * @param levelSet all levels (product types).
   * @param satelliteSet all satellites (Missions).
   * @param productsRemoved all removed products
   */
  const saveProductsAdded = async (
    results: string[],
    levelSet: Set<string>,
    satelliteSet: Set<string>,
    productsRemoved: string[]
  ) => {
    const operationType = productsRemoved.length ? 'REMOVED' : 'ADDED';
    // prepare collectionsDetails
    let products = [
      ...results.filter(
        (result) => !levelSet.has(result) && !satelliteSet.has(result)
      ),
    ];
    // secure that products are unique when saved further in context.
    productsRemoved = Array.from(new Set(productsRemoved));
    products = Array.from(new Set(products));
    // update state to remove the selected products from db
    if (operationType === 'REMOVED') {
      setSelectedProducts({
        productsName: productsRemoved,
        operationType,
      });
    }

    const collectionsDetailsToAdd = formattedCollectionsList.filter(
      (collectionDetails: CollectionDetails) => {
        return products.includes(collectionDetails.productName);
      }
    );

    let previousCollectionsDetails = [
      ...context.productsAdded.collectionsDetails,
    ];

    collectionsDetailsToAdd.forEach((collection) => {
      if (
        !context.productsAdded.collectionsDetails.some(
          (collectionDetails) =>
            collectionDetails.productName === collection.productName
        )
      ) {
        previousCollectionsDetails.push(collection);
      }
    });

    const productsToAdd = {
      products,
      results,
      levelSet,
      satelliteSet,
      collectionsDetails: previousCollectionsDetails,
    } as ProductsAdded;

    // limit to 100 products in myData
    if (productsToAdd?.products?.length <= 100) {
      context.setProductsAdded(productsToAdd);
    } else {
      setMaxProductsWarningVisible(true);
    }
  };

  /**
   * Add All Products From a clicked Satellite.
   * @param products string[] products names.
   * @param satelliteClicked string Satellite Clicked.
   * @returns string[] products.
   */
  const addAllProductsFromSatellite = (
    products: string[],
    satelliteClicked: string
  ) => {
    return Array.from(
      new Set([
        ...products.concat(
          [
            satelliteCollections
              .filter((collection) => collection.satellite === satelliteClicked)
              .map((collection) => collection.title),
            Array.from(
              new Set(
                satelliteCollections
                  .filter(
                    (collection) => collection.satellite === satelliteClicked
                  )
                  .map((collection) => collection.level)
              )
            ),
          ].flat()
        ),
      ])
    );
  };

  /**
   * Clean Levels Below Satellite Clicked.
   * @param products string[] products names.
   * @param levelSet product types (level) unique set.
   * @returns string[] products.
   */
  const cleanLevelsBelowSatelliteClicked = (
    products: string[],
    levelSet: Set<string>
  ) => {
    const levelsToClean = products.filter((result) => {
      if (levelSet.has(result)) {
        const collectionCustom = satelliteCollections.find(
          (collection) => collection.level === result
        );
        return (
          collectionCustom && !products.includes(collectionCustom?.satellite)
        );
      }
      return false;
    });
    levelsToClean.forEach((level) => {
      const indexToClean = products.findIndex((result) => result === level);
      products.splice(indexToClean, 1);
    });

    return products;
  };

  /**
   * Call three Cart-API queries
   * @param queryKey contains the following params:
   * @param user current user
   * @param name product name
   * @param mission product dataType
   * @param urn product ID
   * @param quicklook quicklook url
   * @param products list of products
   * @returns saveProducts
   * @returns getProducts all stored products
   * @returns deleteProducts
   */
  const [saveProducts, getProducts, deleteProducts] = useQueries({
    queries: [
      {
        queryKey: [
          'SaveSelectedProducts',
          {
            payload: selectedProducts.productsName.map((el) => {
              const collection = formattedCollectionsList?.find(
                (collection) => collection.productName === el
              );
              return {
                user: context.userLoggedIn.email,
                name: el,
                storage:
                  collection?.collectionDataModel?.general?.storageMode ?? '',
                downloaded: collection?.checksum,
                mission:
                  collection?.collectionDataModel.productDetails[DATASET] ?? '',
                urn: collection?.id,
                quicklook: collection?.thumbnail ?? collection?.quicklook,
                checksum: collection?.checksum,
                endpointURL: collection?.endpointURL ?? '',
                href: collection?.href ?? '',
                assets: collection?.assets ?? [],
              };
            }),
          },
          navigate,
          context.setError400,
        ],
        queryFn: saveSelectedProducts,
        enabled: false,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
      },
      {
        queryKey: [
          'GetSavedProducts',
          {
            user: context.userLoggedIn.email,
          },
        ],
        queryFn: getSavedProducts,
        retry: false,
        enabled: !isUserNotLogged,
      },
      {
        queryKey: [
          'DeleteSavedProduct',
          {
            payload: selectedProducts.productsName.map((el) => {
              const collection = formattedCollectionsList?.find(
                (collection) => collection.productName === el
              );
              return {
                user: context.userLoggedIn.email,
                name: el,
                downloaded: collection?.checksum,
                mission:
                  collection?.collectionDataModel.productDetails[DATASET],
                urn: collection?.id,
                quicklook: collection?.thumbnail ?? collection?.quicklook,
              };
            }),
          },
        ],
        queryFn: deleteSavedProduct,
        enabled: false,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
      },
    ],
  });

  /**
   * @requires selectedProducts
   * @description
   *  Trigger SaveSelectedProducts API to store selected products
   *  OR
   *  Trigger DeleteSavedProduct API to remove selected products
   */
  useEffect(() => {
    if (selectedProducts.productsName.length > 0) {
      if (selectedProducts.operationType === 'ADDED') {
        saveProducts.refetch().then((res) => {
          if (res.isSuccess) {
            showToastWhenProductsAddedOrRemoved(
              res.data,
              selectedProducts.operationType
            );
          }
        });

        selectedProducts.productsName
          .filter((productName) => {
            return (
              formattedCollectionsList?.find(
                (collection) => collection.productName === productName
              )?.collectionDataModel?.general?.storageMode === 'Datalake Tier 3'
            );
          })
          .forEach((productName) => {
            restoreArchivedProduct({
              queryKey: [
                'RestoreArchivedProduct',
                {
                  payload: {
                    product: productName,
                    process: 'download',
                  },
                },
              ],
            } as QueryFunctionContext<any>).catch((error) =>
              console.error('recovering request failed with error : ', error)
            );
          });
      } else if (selectedProducts.operationType === 'REMOVED') {
        deleteProducts.refetch().then((res) => {
          if (res.isSuccess) {
            showToastWhenProductsAddedOrRemoved(
              res.data,
              selectedProducts.operationType
            );
          }
        });
      }
    }
    // eslint-disable-next-line
  }, [selectedProducts]);

  /**
   * @description Trigger GetSavedProducts API to get all stored products and use it to fill the context
   * @requires data
   */
  useEffect(() => {
    if (getProducts.data && getProducts.data.length > 0) {
      const productsToAdd = getProductsAddedObject(getProducts.data);
      context.setProductsAdded(productsToAdd);
    } else if (!context.productsAdded.products.length) {
      const productsToAdd = {
        products: [],
        results: [],
        levelSet: new Set(),
        satelliteSet: new Set(),
        collectionsDetails: [] as CollectionDetails[],
      } as ProductsAdded;
      context.setProductsAdded(productsToAdd);
    }
    // eslint-disable-next-line
  }, [getProducts.data]);

  /**
   * Update state to persist the new selected product into db
   * @param allProducts exist in the context plus the new selected ones
   * @param selectedProducts only the new selected products
   * @param levelSet level
   * @param satelliteSet satellite
   * @return void
   */
  const selectedProductsPersistence = (
    allProducts: Array<string>,
    selectedProducts: Array<string>,
    levelSet: Set<string>,
    satelliteSet: Set<string>
  ) => {
    let allCleanProducts = [
      ...allProducts.filter(
        (product) => !levelSet.has(product) && !satelliteSet.has(product)
      ),
    ];
    if (Array.from(new Set(allCleanProducts)).length <= 100) {
      let cleanSelectedProductsProducts = selectedProducts.filter(
        (productItem) =>
          !levelSet.has(productItem) &&
          !satelliteSet.has(productItem) &&
          !context.productsAdded.products.find((el) => el === productItem)
      );

      setSelectedProducts({
        productsName: Array.from(new Set(cleanSelectedProductsProducts)),
        operationType: 'ADDED',
      });
    }
  };

  /**
   * add product (item) to the list of selected products by user.
   * @param e click event
   * @param label The product label clicked.
   * @returns void
   */
  const addProduct = (e: any, label: any) => {
    e.stopPropagation();
    if (isUserNotLogged) return;
    const elementNameClicked = label.props.children;
    let [levelSet, satelliteSet] = buildResultsSets(
      context,
      satelliteCollections
    );
    let productsRemoved: string[] = [];
    let products: string[];

    if (context.productsAdded.results.includes(elementNameClicked)) {
      products = context.productsAdded.results.filter(
        (el) => el !== elementNameClicked
      );
    } else {
      products = [...context.productsAdded.results, elementNameClicked];
      selectedProductsPersistence(
        products,
        [elementNameClicked],
        levelSet,
        satelliteSet
      );
    }

    if (
      levelSet.has(elementNameClicked) &&
      !context.productsAdded.results.includes(elementNameClicked)
    ) {
      products = addAllProductsFromProductType(
        products,
        elementNameClicked,
        satelliteCollections
      );
      selectedProductsPersistence(
        products,
        addAllProductsFromProductType(
          [],
          elementNameClicked,
          satelliteCollections
        ),
        levelSet,
        satelliteSet
      );
    } else if (
      levelSet.has(elementNameClicked) &&
      context.productsAdded.results.includes(elementNameClicked)
    ) {
      [productsRemoved, products] = removeAllProductsFromNode(
        products,
        elementNameClicked,
        'level',
        satelliteCollections,
        context
      );
    } else if (
      satelliteSet.has(elementNameClicked) &&
      !context.productsAdded.results.includes(elementNameClicked)
    ) {
      products = addAllProductsFromSatellite(products, elementNameClicked);
      selectedProductsPersistence(
        products,
        addAllProductsFromSatellite([], elementNameClicked),
        levelSet,
        satelliteSet
      );
    } else if (
      satelliteSet.has(elementNameClicked) &&
      context.productsAdded.results.includes(elementNameClicked)
    ) {
      [productsRemoved, products] = removeAllProductsFromNode(
        products,
        elementNameClicked,
        'satellite',
        satelliteCollections,
        context
      );

      //clean levels linked to the removed satellite
      products = cleanLevelsBelowSatelliteClicked(products, levelSet);
    } else if (context.productsAdded?.products?.includes(elementNameClicked)) {
      // remove one product
      productsRemoved = [elementNameClicked];
    }

    saveProductsAdded(products, levelSet, satelliteSet, productsRemoved);
  };

  /**
   * Calculate Product Position.
   * @param selectedProductName product name from selected product by user.
   */
  const calculateProductPosition = (selectedProductName: string) => {
    const productNode = productRefs.current[selectedProductName];
    const container = document.querySelector('.results-container');
    const scrollPanel = document.querySelector(
      '.results-container .p-scrollpanel-content'
    );
    if (productNode && container && scrollPanel) {
      const offsetPosition = productNode.offsetTop - 150;
      scrollPanel.scrollTop = offsetPosition;
    }
  };

  /**
   * save the features from items search when data change.
   */
  useEffect(() => {
    if (data)
      context.setSearchedFeatures(data?.features, data?.numberMatched || 0);
    // eslint-disable-next-line
  }, [data]);

  /**
   * onPageChange defines elements to display on each product page.
   * @param event Page change event.
   */
  const onPageChange = (event: any) => {
    setFirst(event.first);
    setRows(event.rows);
    setPage(event.page + 1);
    context.setQueryPage(event.page + 1);
    let nexttoken = null
    if (event.page < context.queryPage) {
      nexttoken = data?.links?.[3]?.body?.token;
    } else {
      nexttoken = data?.links?.[2]?.body?.token;
    }
    //const nexttoken = data?.links?.[2]?.body?.token;
    //const previoustoken = data?.links?.[3]?.body?.token;
    setNextToken(nexttoken);
  };
  const isEmptyData = Boolean(
    !data || data?.length === 0 || data?.numberMatched === 0
  );

  if (isFetching) {
    return (
      <div
        data-testid="loading-results"
        className="card flex justify-content-center"
      >
        <ProgressSpinner />
      </div>
    );
  }
  if (isEmptyData) {
    return (
      <div className="empty-results">
        <img
          alt="Empty results"
          src={require('../../../../assets/icons/emptyResults.PNG')}
        />
        <h2>Whoops !</h2>
        <h4> No results found for current search filters</h4>
        <h3>Please try again</h3>
      </div>
    );
  }
  return (
    <div className="results-container">
      <Paginator
        first={first}
        rows={rows}
        totalRecords={data?.numberMatched}
        onPageChange={onPageChange}
        template={PaginatorTemplate}
        pageLinkSize={0}
        currentPageReportTemplate='Page {currentPage}'
        className="results-paginator"
      />
      <div className="resultsTab card flex justify-content-center">
        <ScrollPanel
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          <Tree
            value={treeNodes}
            nodeTemplate={(node, options) => (
              <NodeTemplate
                node={node}
                options={options}
                isUserNotLogged={isUserNotLogged}
                isDatalakeAvailable={isDatalakeAvailable}
                collectionsList={formattedCollectionsList}
                addProduct={addProduct}
                productRefs={productRefs}
              />
            )}
            togglerTemplate={(node, options) => (
              <TogglerTemplate node={node} options={options} />
            )}
            selectionMode="single"
            expandedKeys={keysToExpand}
            className="results-tree"
          />
        </ScrollPanel>
      </div>
      <Toast ref={toast} position="top-center" className="results-toast" />
    </div>
  );
};
